export class GatewayError extends Error {
  code?: string;
  details?: {
    code?: string;
    requestId?: string;
    [key: string]: unknown;
  };

  constructor(message: string, error?: Record<string, unknown>) {
    super(message);
    this.name = 'GatewayError';
    if (error) {
      this.code = typeof error.code === 'string' ? error.code : undefined;
      if (error.details && typeof error.details === 'object') {
        this.details = error.details as GatewayError['details'];
      }
    }
  }
}

export interface DeviceConnectParams {
  id: string;
  publicKey: string;
  signature: string;
  signedAt: number;
  nonce: string | undefined;
}

export interface SignDeviceContext {
  nonce: string | null;
  clientId: string;
  clientMode: string;
  role: string;
  scopes: string[];
  token?: string | null;
}

export interface GatewayClientOptions {
  url: string;
  token?: string;
  password?: string;
  /** Optional role for connection. Defaults to 'operator'. */
  role?: string;
  /** Optional scopes for connection. Defaults to ['operator.read', 'operator.write']. */
  scopes?: string[];
  /** Optional Origin header for Node.js WebSocket connections (browsers set this automatically). */
  origin?: string;
  /** Client ID sent in connect params. Defaults to 'webchat-ui'. */
  clientId?: string;
  /** Client mode sent in connect params. Defaults to 'ui'. */
  clientMode?: string;
  /** Platform identifier. Defaults to 'web'. */
  platform?: string;
  /** Device family identifier (e.g. 'node', 'web'). */
  deviceFamily?: string;
  /** Callback to sign a challenge nonce and return device identity params. */
  signDevice?: (ctx: SignDeviceContext) => Promise<DeviceConnectParams>;
  onEvent?: (event: GatewayEvent) => void;
  onChatEvent?: (event: ChatEvent) => void;
  onAgentEvent?: (event: AgentEvent) => void;
  onConnected?: (payload: any) => void;
  onDisconnected?: (reason: string) => void;
  onError?: (error: Error) => void;
}

export interface GatewayEvent {
  event: string;
  payload: any;
  seq?: number;
}

export interface ChatEvent {
  state: 'delta' | 'final' | 'error' | 'aborted' | 'context_injected' | 'user';
  message?: unknown;
  sessionKey?: string;
  runId?: string;
  errorMessage?: string;
}

export interface Attachment {
  type?: string;
  mimeType: string;
  fileName?: string;
  content: string; // base64 (no data: prefix)
}

export interface AgentEvent {
  runId: string;
  stream: 'assistant' | 'tool' | 'lifecycle' | 'thinking' | (string & {});
  seq: number;
  sessionKey?: string;
  ts?: number;
  data: Record<string, any>;
}

export interface AgentIdentity {
  name: string;
  theme?: string;
  emoji?: string;
  avatar?: string;
  avatarUrl?: string;
}

export interface AgentInfo {
  id: string;
  name?: string;
  identity?: AgentIdentity;
}

export interface ResetConfirmRequiredPayload {
  nonce: string;
  sessionKey: string;
  reason: string;
  senderId?: string;
  source?: string;
  expiresAt: number;
}

export interface ResetCancelledPayload {
  nonce: string;
  reason: 'timeout' | 'denied' | (string & {});
}

export interface ResetAppliedPayload {
  nonce: string;
  sessionKey: string;
}

export interface AgentsListResponse {
  defaultId: string;
  mainKey: string;
  scope: string;
  agents: AgentInfo[];
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  [key: string]: any;
}

type GatewayRequestFrame = {
  type: 'req';
  id: string;
  method: string;
  params: Record<string, any>;
};

type GatewayResponseFrame = {
  type: 'res';
  id: string;
  ok: boolean;
  payload?: any;
  error?: {
    message?: string;
    [key: string]: any;
  };
};

type GatewayEventFrame = {
  type: 'event';
  event: string;
  payload: any;
  seq?: number;
};

type GatewayPongFrame = {
  type: 'pong';
};

type GatewayFrame = GatewayResponseFrame | GatewayEventFrame | GatewayPongFrame;

type PendingRequest = {
  resolve: (payload: any) => void;
  reject: (error: Error) => void;
};

const CONNECT_CHALLENGE_EVENT = 'connect.challenge';
const CHAT_EVENT = 'chat';
const AGENT_EVENT = 'agent';

const INITIAL_RECONNECT_DELAY_MS = 800;
const MAX_RECONNECT_DELAY_MS = 15_000;
const RECONNECT_FACTOR = 1.7;
const CONNECT_CHALLENGE_TIMEOUT_MS = 2_000;
const PING_INTERVAL_MS = 30_000;
const PONG_TIMEOUT_MS = 10_000;

export class GatewayClient {
  private readonly options: GatewayClientOptions;
  private ws: WebSocket | null = null;
  private pendingRequests = new Map<string, PendingRequest>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private challengeTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
  private manuallyDisconnected = false;
  private isConnected = false;
  private connectInFlight: Promise<void> | null = null;
  private sentConnect = false;
  private challengeNonce: string | null = null;
  private deviceToken: string | null = null;

  constructor(options: GatewayClientOptions) {
    this.options = options;
  }

  connect(): void {
    this.manuallyDisconnected = false;
    this.clearReconnectTimer();

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      this.ws = this.options.origin
        ? new WebSocket(this.options.url, { headers: { Origin: this.options.origin } } as any)
        : new WebSocket(this.options.url);
    } catch (error) {
      this.emitError(this.toError(error));
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.handleOpen();
    };

    this.ws.onmessage = (event) => {
      void this.handleRawMessage(event.data);
    };

    this.ws.onerror = () => {
      this.emitError(new Error('WebSocket error'));
    };

    this.ws.onclose = (event) => {
      this.handleClose(event);
    };
  }

  disconnect(): void {
    this.manuallyDisconnected = true;
    this.clearReconnectTimer();
    this.stopPingTimer();
    this.clearChallengeTimer();
    this.clearPongTimeout();
    this.connectInFlight = null;
    this.challengeNonce = null;

    this.isConnected = false;
    this.rejectPendingRequests(new Error('Disconnected by client'));

    if (this.ws) {
      try {
        this.ws.close(1000, 'client disconnect');
      } catch {
        // no-op
      }
      this.ws = null;
    }
  }

  get connected(): boolean {
    return this.isConnected && !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /** Set a previously issued device token for reconnection. */
  setDeviceToken(token: string | null): void {
    this.deviceToken = token;
  }

  /** Get the current device token (issued by gateway after pairing). */
  getDeviceToken(): string | null {
    return this.deviceToken;
  }

  request<T = any>(method: string, params: Record<string, any> = {}): Promise<T> {
    if (!this.connected) {
      return Promise.reject(new Error('Gateway is not connected'));
    }

    return this.sendRequest<T>(method, params);
  }

  async sendMessage(sessionKey: string, message: string, attachments?: Attachment[]): Promise<string> {
    const idempotencyKey = this.createUuid();

    const params: Record<string, any> = {
      sessionKey,
      message,
      deliver: false,
      idempotencyKey,
    };

    if (attachments && attachments.length > 0) {
      params.attachments = attachments;
    }

    await this.request('chat.send', params);
    return idempotencyKey;
  }

  async getHistory(
    sessionKey: string,
    limit = 100,
  ): Promise<{ messages: ChatMessage[]; thinkingLevel?: string }> {
    const payload = await this.request<any>('chat.history', { sessionKey, limit });

    if (Array.isArray(payload)) {
      return { messages: payload as ChatMessage[] };
    }

    return {
      messages: Array.isArray(payload?.messages) ? payload.messages : [],
      thinkingLevel: payload?.thinkingLevel,
    };
  }

  async getPipelineHistory(
    pipelineId: string,
  ): Promise<{ messages: ChatMessage[]; thinkingLevel?: string }> {
    const payload = await this.request<any>('pipeline.history', { pipeline_id: pipelineId });

    return {
      messages: Array.isArray(payload?.messages) ? payload.messages : [],
      thinkingLevel: payload?.thinkingLevel,
    };
  }

  async listSessions(activeMinutes?: number): Promise<any[]> {
    const params = typeof activeMinutes === 'number' ? { activeMinutes } : {};
    const payload = await this.request<any>('sessions.list', params);

    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.sessions)) {
      return payload.sessions;
    }

    return [];
  }

  async listAgents(): Promise<AgentsListResponse> {
    return this.request<AgentsListResponse>('agents.list', {});
  }

  async abortChat(sessionKey: string): Promise<{ ok: boolean; status?: string; runId?: string }> {
    const res = await this.request<{ ok?: boolean; status?: string; runId?: string }>('chat.abort', { sessionKey });
    return {
      ok: res?.ok !== false,
      status: res?.status,
      runId: res?.runId,
    };
  }

  async sendResetDecision(decision: 'confirm' | 'deny', nonce: string): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.connected) {
      throw new Error('Gateway is not connected');
    }

    const trimmedNonce = nonce.trim();
    if (!trimmedNonce) {
      throw new Error('Reset decision nonce is required');
    }

    const frame = {
      type: decision === 'confirm' ? 'reset_confirm' : 'reset_deny',
      nonce: trimmedNonce,
    };

    this.ws.send(JSON.stringify(frame));
  }

  async setSessionVerbose(
    sessionKey: string,
    level: 'off' | 'summary' | 'full' = 'summary',
  ): Promise<void> {
    await this.request('sessions.patch', { key: sessionKey, verboseLevel: level });
  }

  private handleOpen(): void {
    this.sentConnect = false;
    this.clearChallengeTimer();
    this.clearPongTimeout();

    this.challengeTimer = setTimeout(() => {
      void this.sendConnectRequest();
    }, CONNECT_CHALLENGE_TIMEOUT_MS);
  }

  private async handleRawMessage(data: unknown): Promise<void> {
    this.noteServerActivity();

    const frame = await this.parseFrame(data);
    if (!frame) {
      return;
    }

    if (frame.type === 'pong') {
      return;
    }

    if (frame.type === 'res') {
      this.handleResponseFrame(frame);
      return;
    }

    this.handleEventFrame(frame);
  }

  private handleResponseFrame(frame: GatewayResponseFrame): void {
    const pending = this.pendingRequests.get(frame.id);
    if (!pending) {
      return;
    }

    this.pendingRequests.delete(frame.id);

    if (frame.ok) {
      pending.resolve(frame.payload);
      return;
    }

    const message = frame.error?.message ?? 'Gateway request failed';
    pending.reject(new GatewayError(message, frame.error));
  }

  private handleEventFrame(frame: GatewayEventFrame): void {
    if (frame.event === CONNECT_CHALLENGE_EVENT) {
      this.challengeNonce = frame.payload?.nonce ?? null;
      if (!this.sentConnect) {
        void this.sendConnectRequest();
      }
      return;
    }

    if (frame.event === CHAT_EVENT) {
      const chatEvt = this.toChatEvent(frame.payload);
      console.debug('[GW:chat]', chatEvt.state, 'key:', chatEvt.sessionKey);
      this.options.onChatEvent?.(chatEvt);
      return;
    }

    if (frame.event === AGENT_EVENT) {
      const agentEvt: AgentEvent = {
        runId: frame.payload?.runId,
        stream: frame.payload?.stream ?? 'lifecycle',
        seq: frame.payload?.seq ?? 0,
        sessionKey: frame.payload?.sessionKey,
        ts: frame.payload?.ts,
        data: frame.payload?.data ?? {},
      };
      this.options.onAgentEvent?.(agentEvt);
      return;
    }

    this.options.onEvent?.({
      event: frame.event,
      payload: frame.payload,
      seq: frame.seq,
    });
  }

  private sendConnectRequest(): Promise<void> {
    if (this.connectInFlight) {
      return this.connectInFlight;
    }

    this.sentConnect = true;
    this.clearChallengeTimer();

    this.connectInFlight = (async () => {
      const params = await this.buildConnectParams();
      return this.sendRequest<any>('connect', params);
    })()
      .then((payload) => {
        this.isConnected = true;
        this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
        this.startPingTimer();

        console.debug('[GatewayClient] Connect OK — deviceToken:', payload?.auth?.deviceToken ? 'issued' : 'none', 'protocol:', payload?.protocol);

        // Persist device token if gateway issued one
        if (payload?.auth?.deviceToken) {
          this.deviceToken = payload.auth.deviceToken;
        }

        this.options.onConnected?.(payload);
      })
      .catch((error) => {
        console.error('[GatewayClient] Connect FAILED:', error);
        this.isConnected = false;
        this.emitError(this.toError(error));

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.close(4001, 'connect failed');
        }
      })
      .finally(() => {
        this.connectInFlight = null;
      });

    return this.connectInFlight;
  }

  private sendRequest<T = any>(method: string, params: Record<string, any> = {}): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('WebSocket is not open'));
    }

    const id = this.createUuid();
    const frame: GatewayRequestFrame = {
      type: 'req',
      id,
      method,
      params,
    };

    return new Promise<T>((resolve, reject) => {
      this.pendingRequests.set(id, {
        resolve,
        reject,
      });

      try {
        this.ws?.send(JSON.stringify(frame));
      } catch (error) {
        this.pendingRequests.delete(id);
        reject(this.toError(error));
      }
    });
  }

  private handleClose(event: CloseEvent): void {
    this.stopPingTimer();
    this.clearChallengeTimer();
    this.clearPongTimeout();
    this.connectInFlight = null;

    const reason = event.reason || 'socket closed';

    this.isConnected = false;
    this.ws = null;

    this.rejectPendingRequests(new Error(`Disconnected: ${reason}`));
    this.options.onDisconnected?.(reason);

    if (!this.manuallyDisconnected) {
      this.scheduleReconnect();
    }
  }

  private startPingTimer(): void {
    this.stopPingTimer();
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
          this.armPongTimeout();
        } catch {
          // If ping send fails, close handler will trigger reconnect logic.
        }
      }
    }, PING_INTERVAL_MS);
  }

  private stopPingTimer(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    this.clearPongTimeout();
  }

  private armPongTimeout(): void {
    this.clearPongTimeout();

    this.pongTimeoutTimer = setTimeout(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.clearPongTimeout();
        return;
      }

      console.warn('[GatewayClient] Ping timed out — forcing reconnect');
      this.emitError(new Error('Gateway pong timeout'));

      try {
        this.ws.close(4000, 'pong timeout');
      } catch {
        // no-op
      }
    }, PONG_TIMEOUT_MS);
  }

  private clearPongTimeout(): void {
    if (this.pongTimeoutTimer) {
      clearTimeout(this.pongTimeoutTimer);
      this.pongTimeoutTimer = null;
    }
  }

  private noteServerActivity(): void {
    this.clearPongTimeout();
  }

  private scheduleReconnect(): void {
    if (this.manuallyDisconnected || this.reconnectTimer) {
      return;
    }

    const delay = this.reconnectDelayMs;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);

    this.reconnectDelayMs = Math.min(
      MAX_RECONNECT_DELAY_MS,
      Math.round(delay * RECONNECT_FACTOR),
    );
  }

  private rejectPendingRequests(error: Error): void {
    for (const [, pending] of this.pendingRequests) {
      pending.reject(error);
    }

    this.pendingRequests.clear();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearChallengeTimer(): void {
    if (this.challengeTimer) {
      clearTimeout(this.challengeTimer);
      this.challengeTimer = null;
    }
  }

  private async buildConnectParams(): Promise<Record<string, any>> {
    const auth: Record<string, string> = {};

    if (this.options.token) {
      auth.token = this.options.token;
    }

    if (this.options.password) {
      auth.password = this.options.password;
    }

    const clientId = this.options.clientId ?? 'webchat-ui';
    const clientMode = this.options.clientMode ?? 'ui';
    const role = this.options.role ?? 'operator';
    const scopes = this.options.scopes ?? ['operator.read', 'operator.write'];
    const platform = this.options.platform ?? 'web';
    const deviceFamily = this.options.deviceFamily ?? undefined;

    // Sign the challenge nonce if device identity callback is provided
    let device: DeviceConnectParams | undefined;
    if (this.options.signDevice) {
      console.debug('[GatewayClient] Signing device challenge, nonce:', this.challengeNonce ? 'present' : 'null');
      try {
        device = await this.options.signDevice({
          nonce: this.challengeNonce,
          clientId,
          clientMode,
          role,
          scopes,
          token: this.options.token ?? null,
        });
        console.debug('[GatewayClient] Device signed OK, id:', device.id, 'hasSignature:', !!device.signature);
      } catch (error) {
        console.error('[GatewayClient] Failed to sign device challenge:', error);
      }
    } else {
      console.debug('[GatewayClient] No signDevice callback — connecting without device identity');
    }

    if (!device) {
      console.warn('[GatewayClient] Connecting WITHOUT device params — device will not be paired');
    }

    const params: Record<string, any> = {
      minProtocol: 3,
      maxProtocol: 3,
      client: {
        id: clientId,
        version: '0.1.0',
        platform,
        ...(deviceFamily ? { deviceFamily } : {}),
        mode: clientMode,
      },
      role,
      scopes,
      caps: ['tool-events'],
      commands: [],
      permissions: {},
      device,
      auth,
      locale: 'en-US',
      userAgent: 'clawlet/0.1.0',
    };

    return params;
  }

  private async parseFrame(data: unknown): Promise<GatewayFrame | null> {
    let text: string;

    if (typeof data === 'string') {
      text = data;
    } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
      text = await data.text();
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(data);
    } else if (ArrayBuffer.isView(data)) {
      text = new TextDecoder().decode(data);
    } else {
      this.emitError(new Error('Unsupported WebSocket message format'));
      return null;
    }

    try {
      const parsed = JSON.parse(text) as GatewayFrame;
      if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) {
        return null;
      }

      if (parsed.type !== 'res' && parsed.type !== 'event' && parsed.type !== 'pong') {
        return null;
      }

      return parsed;
    } catch {
      this.emitError(new Error('Failed to parse gateway frame'));
      return null;
    }
  }

  private toChatEvent(payload: any): ChatEvent {
    const state = this.normalizeChatState(payload?.state);
    const rawMessage = payload?.message ?? payload?.payload?.message;

    return {
      state,
      message: rawMessage ?? this.extractMessageText(payload),
      sessionKey: payload?.sessionKey,
      runId: payload?.runId,
      errorMessage: payload?.errorMessage,
    };
  }

  private normalizeChatState(value: unknown): ChatEvent['state'] {
    if (value === 'delta' || value === 'final' || value === 'error' || value === 'aborted' || value === 'context_injected' || value === 'user') {
      return value;
    }

    // Unknown/missing states should NOT default to 'error' - that causes false error
    // UI states (e.g., subagent pills turning red incorrectly). Default to 'delta'
    // which is the safest no-op state for streaming updates.
    if (value != null) {
      console.warn('[GatewayClient] Unknown chat state:', value, '- treating as delta');
    }
    return 'delta';
  }

  private extractMessageText(payload: any): string | undefined {
    const msg = payload?.message;

    // String message (simple case)
    if (typeof msg === 'string') {
      return msg;
    }

    // Structured message: { role, content: [{ type: "text", text: "..." }], timestamp }
    if (msg && typeof msg === 'object' && !Array.isArray(msg)) {
      const content = msg.content;
      if (typeof content === 'string') {
        return content;
      }
      if (Array.isArray(content)) {
        const texts = content
          .filter((block: any) => block?.type === 'text' && typeof block.text === 'string')
          .map((block: any) => block.text);
        if (texts.length > 0) {
          return texts.join('\n');
        }
      }
      if (typeof msg.text === 'string') {
        return msg.text;
      }
    }

    // Fallback: top-level content or nested payload
    if (typeof payload?.content === 'string') {
      return payload.content;
    }

    if (typeof payload?.payload?.message === 'string') {
      return payload.payload.message;
    }

    return undefined;
  }

  private createUuid(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const rand = Math.floor(Math.random() * 16);
      const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private emitError(error: Error): void {
    this.options.onError?.(error);
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    return new Error(typeof error === 'string' ? error : 'Unknown error');
  }
}

export type EventType =
  | 'message'
  | 'thinking'
  | 'toolCall'
  | 'toolResult'
  | 'subagentSpawn'
  | 'subagentComplete'
  | 'cost'
  | 'error'
  | 'connected'
  | 'disconnected';
